import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Phone } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const FAQS = [
    {
        q: "Quel est votre pourcentage ?",
        a: "Notre commission est unique et simple : 12 % du montant de vos réservations. Aucun frais fixe : pas de réservation, pas de commission.",
        keys: ["pourcentage", "commission", "prix", "tarif", "coût", "combien", "%"],
    },
    {
        q: "Et le permis CITQ ?",
        a: "La location courte durée au Québec exige un numéro d'enregistrement de la CITQ. Nous vous accompagnons dans les démarches et veillons à ce que votre numéro soit bien affiché sur toutes vos annonces.",
        keys: ["citq", "permis", "enregistrement", "loi", "légal", "réglementation", "reglementation", "numéro"],
    },
    {
        q: "Comment se passe la remise des clés ?",
        a: "Nos concierges accueillent chaque voyageur en personne, 7j/7 : remise des clés, visite du logement, état des lieux. Vous n'avez jamais à vous déplacer.",
        keys: ["clé", "cles", "clés", "remise", "arrivée", "check-in", "checkin", "accueil", "état des lieux"],
    },
    {
        q: "Puis-je utiliser mon logement quand je veux ?",
        a: "Absolument. Vous bloquez vos dates en un message et le calendrier se synchronise sur toutes les plateformes. Votre logement reste le vôtre, en priorité.",
        keys: ["utiliser", "bloquer", "calendrier", "dates", "disponibilité", "occuper", "venir"],
    },
    {
        q: "Qui s'occupe du ménage ?",
        a: "Nos équipes de ménage hôtelier interviennent après chaque départ : ménage complet, literie et serviettes impeccables, produits d'accueil. Vous n'avez rien à prévoir.",
        keys: ["ménage", "menage", "linge", "nettoyage", "propre", "draps"],
    },
];

const GREETING = {
    from: "bot",
    text: "Bonjour, je suis Pauline, concierge Gestion Séjoura. Une question sur la gestion de votre Airbnb au Québec ?",
};
const FALLBACK =
    "Excellente question ! Pour y répondre précisément, laissez-nous vos coordonnées via le formulaire de contact — un concierge vous rappelle sous 24 h. Ou appelez-nous au +1 438-397-6147.";

export default function LiveChatWidget() {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([GREETING]);
    const [input, setInput] = useState("");
    const bottomRef = useRef(null);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, open]);

    const answer = (text) => {
        const lower = text.toLowerCase();
        const hit = FAQS.find((f) => f.keys.some((k) => lower.includes(k)));
        return hit ? hit.a : FALLBACK;
    };

    const push = (question) => {
        const q = question.trim();
        if (!q) return;
        setMessages((m) => [...m, { from: "user", text: q }]);
        setInput("");
        setTimeout(() => setMessages((m) => [...m, { from: "bot", text: answer(q) }]), 450);
    };

    return (
        <div className="fixed bottom-6 right-6 z-[60] flex flex-col items-end gap-4">
            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="livechat-panel"
                        initial={{ opacity: 0, y: 24, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 24, scale: 0.95 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="flex h-[460px] w-[340px] flex-col overflow-hidden rounded-2xl border border-[#8A7563]/25 bg-[#FFFFFF]/95 shadow-[0_24px_80px_rgba(90,79,68,0.22)] backdrop-blur-xl sm:w-[380px]"
                    >
                        <div className="flex items-center justify-between border-b border-[#2B2622]/10 bg-[#FAF8F5]/80 px-5 py-4">
                            <div className="flex items-center gap-3">
                                <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-[#8A7563]/15 font-display text-base font-semibold text-[#8A7563]">
                                    P
                                    <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-[#FAF8F5] bg-[#10B981]" />
                                </span>
                                <div>
                                    <p className="text-sm font-semibold">Pauline — Gestion Séjoura</p>
                                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#10B981]">En ligne</p>
                                </div>
                            </div>
                            <button data-testid="livechat-close-button" onClick={() => setOpen(false)} className="text-[#8A7F72] transition-colors hover:text-[#8A7563]" aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div data-lenis-prevent className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
                            {messages.map((m, i) => (
                                <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                                    <p
                                        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[13px] leading-relaxed ${
                                            m.from === "user"
                                                ? "rounded-br-sm bg-[#8A7563] text-[#FAF8F5]"
                                                : "rounded-bl-sm border border-[#2B2622]/10 bg-[#FAF8F5]/70 text-[#5A4F44]"
                                        }`}
                                    >
                                        {m.text}
                                    </p>
                                </div>
                            ))}
                            <div ref={bottomRef} />
                        </div>

                        <div className="border-t border-[#2B2622]/10 px-4 pt-3">
                            <div className="flex flex-wrap gap-2">
                                {FAQS.map((f, i) => (
                                    <button
                                        key={f.q}
                                        data-testid={`livechat-option-faq-${i + 1}`}
                                        onClick={() => push(f.q)}
                                        className="rounded-full border border-[#8A7563]/30 px-3 py-1.5 text-[11px] text-[#8A7563] transition-all duration-300 hover:bg-[#8A7563] hover:text-[#FAF8F5]"
                                    >
                                        {f.q}
                                    </button>
                                ))}
                            </div>
                            <form
                                className="flex items-center gap-2 py-3"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    push(input);
                                }}
                            >
                                <input
                                    data-testid="livechat-input"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder="Écrivez votre question…"
                                    className="flex-1 rounded-full border border-[#2B2622]/10 bg-[#FAF8F5]/70 px-4 py-2.5 text-[13px] text-[#2B2622] placeholder:text-[#8A7F72]/60 focus:border-[#8A7563]/50 focus:outline-none"
                                />
                                <button
                                    data-testid="livechat-send-message"
                                    type="submit"
                                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#8A7563] text-[#FAF8F5] transition-colors hover:bg-[#6E5D4E]"
                                    aria-label="Envoyer"
                                >
                                    <Send size={14} />
                                </button>
                            </form>
                            <button
                                data-testid="livechat-contact-button"
                                onClick={() => {
                                    setOpen(false);
                                    scrollToSection("#contact");
                                }}
                                className="mb-3 flex w-full items-center justify-center gap-2 rounded-full border border-[#2B2622]/10 py-2 text-[11px] text-[#8A7F72] transition-colors hover:border-[#8A7563]/40 hover:text-[#8A7563]"
                            >
                                <Phone size={12} /> Être rappelé par un concierge
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.button
                data-testid="livechat-trigger-button"
                onClick={() => setOpen(!open)}
                whileHover={{ scale: 1.06 }}
                whileTap={{ scale: 0.94 }}
                className="flex h-14 w-14 items-center justify-center rounded-full bg-[#8A7563] text-[#FAF8F5] shadow-[0_12px_40px_rgba(138,117,99,0.4)]"
                aria-label="Ouvrir le chat"
            >
                {open ? <X size={22} /> : <MessageCircle size={22} />}
            </motion.button>
        </div>
    );
}
